import { useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { TabPanel } from '@/shared/components';
import { PositionsTable } from '@/shared/components/boost/PositionsTable';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { useLpStats, useScreenSize } from '@/shared/hooks';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import { CloseCircleIcon } from '@/shared/icons/small';
import type { RpcOrder } from '@/shared/utils';
import CloseAllOrdersModal from '../components/CloseAllOrdersModal';
import Layout from '../components/Layout';
import { OpenOrdersTableMobile, OpenOrdersTable } from '../components/Market/OpenOrdersTable';
import { FeesAndPortfolioChartCard } from '../components/Portfolio/FeesAndPortfolioChartCard';
import { LPPortfolioCards } from '../components/Portfolio/LpPortfolioCards';
import { PortfolioBreakdownCard } from '../components/Portfolio/PortfolioBreakdownCard';
import { QuickActions } from '../components/Portfolio/QuickActions';
import { useOrders } from '../hooks/useOrders';

export default function Portfolio() {
  const { selectedAccount } = usePolkadot(true);
  const { openRpcOrders, processorOpenOrders } = useOrders();
  const router = useRouter();
  const { totalLiquidityUsd, liquidityDataLoading } = useLpStats(selectedAccount?.idSs58);
  const [ordersToClose, setOrdersToClose] = useState<RpcOrder[]>([]);

  const { isMobile } = useScreenSize();

  return (
    <Layout isMobileView={isMobile} title="Portfolio">
      <div className="flex flex-col gap-y-6">
        {!isMobile && <QuickActions />}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="col-span-1 flex flex-col lg:col-span-2">
            <PortfolioBreakdownCard isMobileView={isMobile} />
          </div>
          <div className="col-span-1 lg:col-span-3">
            <FeesAndPortfolioChartCard
              account={selectedAccount.idSs58}
              currentTotalLiquidity={totalLiquidityUsd}
              totalLiquidityLoading={liquidityDataLoading}
              isMobileView={isMobile}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          <LPPortfolioCards />
        </div>
        <div className={classNames(isMobile && 'mb-7')}>
          {isMobile ? (
            <OpenOrdersTableMobile
              rpcOrders={openRpcOrders}
              processorOrders={processorOpenOrders?.account?.openOrders.nodes}
            />
          ) : (
            <TabPanel
              nodes={[
                {
                  name: 'Boost Positions',
                  content: (
                    <PositionsTable
                      accountIdSs58={selectedAccount.idSs58}
                      onRowClick={(asset, feeTierPips) => {
                        router.push(`/boost/pools/${asset}/${feeTierPips}?table=Boosted+Deposits`);
                      }}
                    />
                  ),
                },
                {
                  name: 'Open Orders',
                  count: openRpcOrders.length > 99 ? '99+' : openRpcOrders.length,
                  content: (
                    <OpenOrdersTable
                      rpcOrders={openRpcOrders}
                      processorOrders={processorOpenOrders?.account?.openOrders.nodes}
                    />
                  ),
                  contentRight: (
                    <div className="px-2">
                      <ReadonlyButton
                        type="secondary-standard"
                        size="small"
                        disabled={openRpcOrders.length === 0}
                        onClick={() => setOrdersToClose(openRpcOrders)}
                      >
                        <div className="flex items-center gap-1">
                          <CloseCircleIcon className="text-cf-red-2 group-disabled:text-cf-gray-5" />{' '}
                          Close all open orders
                        </div>
                      </ReadonlyButton>
                      <CloseAllOrdersModal
                        open={ordersToClose.length > 0}
                        onClose={() => setOrdersToClose([])}
                        ordersToClose={ordersToClose}
                      />
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
